import {ActionIcon, Box, Button, Flex, Group, Loader, Stack, Tooltip, Title} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import ChemicalStructureSVG, {ChemicalStructureImageData} from 'chemistry/ChemicalStructureSVG';
import {formatMolecularFormula} from 'chemistry/molecules/MoleculeUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import PlainSearchInput, {ClearType} from 'ui-library/PlainSearchInput';
import {ScrollableTableColumn, ScrollableTableRow} from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';
import {InputChangeEvent} from 'util/Types';

export interface ChemicalCounterpartItem {
  id: string;
  name: string;
  code: string | null;
  molecularFormula: string | null;
  calculatedMolecularWeight: number | null;
  canonicalSmiles: string;
  imageData: ChemicalStructureImageData;
}

interface ChemicalCounterpartRowSortableData {
  name: string;
  code: string;
  molecularWeight: string | undefined;
}

interface ChemicalCounterpartRowData extends ChemicalCounterpartRowSortableData {
  id: string;
  molecularFormula: string;
  chemicalStructure: React.ReactNode;
  canonicalSmiles: string;
  actions: JSX.Element;
}

interface ChemicalCounterpartRowExtra {
  calculatedMolecularWeight: number | null;
}

interface ChemicalCounterpartRow extends ScrollableTableRow {
  data: ChemicalCounterpartRowData;
  extra: ChemicalCounterpartRowExtra;
}

function ChemicalCounterpartActions({id, name}: {id: string; name: string}): JSX.Element {
  return (
    <Group>
      <Tooltip label="Edit" withinPortal={true}>
        <ActionIcon aria-label="Edit" variant="tertiary" onClick={() => {}}>
          <BenchlingIcon icon="edit" />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={EXPORT_AUDIT_LOG_DISPLAY_STRING} withinPortal={true}>
        <ActionIcon
          aria-label={`${EXPORT_AUDIT_LOG_DISPLAY_STRING} for ${name}`}
          variant="tertiary"
          onClick={() => {
            exportAuditLogs([id]);
          }}
        >
          <BenchlingIcon icon="audit-trail" />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}

// Keep in sync with client/chemistry/molecules/tabs/MoleculePhysicochemicalProperties.tsx
const MOLECULAR_WEIGHT_PRECISION = 3;
const ROW_HEIGHT = 80;
const SIDE_PADDING = 40;
const SIDE_PADDING_PX = `${SIDE_PADDING}px`;

const COLUMNS: Array<ScrollableTableColumn> = [
  {header: 'Name', accessorKey: 'name', flexGrow: 1, isCollapsible: false, isSortable: true, sortKey: 'name'},
  {header: 'Code', accessorKey: 'code', isCollapsible: true, isSortable: true, sortKey: 'code'},
  {
    header: 'Chemical Structure',
    accessorKey: 'chemicalStructure',
    flexGrow: 2,
    isCollapsible: true,
    isSortable: false,
  },
  {
    header: 'Molecular Formula',
    accessorKey: 'molecularFormula',
    isCollapsible: true,
    isSortable: false,
    sortKey: 'molecularFormula',
  },
  {
    header: 'Molecular Weight',
    accessorKey: 'molecularWeight',
    isCollapsible: true,
    isSortable: true,
    sortKey: 'molecularWeight',
  },
  {
    header: 'Canonical SMILES',
    accessorKey: 'canonicalSmiles',
    flexGrow: 2,
    isCollapsible: true,
    isSortable: false,
    wrapText: true,
  },
  {header: 'ID', accessorKey: 'id', isCollapsible: true, isSortable: false, sortKey: 'id', wrapText: true},
  {header: 'Actions', accessorKey: 'actions', isCollapsible: false, isSortable: false},
];

export interface ChemicalCounterpartLibraryProps {
  title: string;
  addNewLabel: string;
  items: Array<ChemicalCounterpartItem>;
  loading: boolean;
  tableKey: string;
  onAddNew: () => void;
  searchPlaceholder: string;
}

export default function ChemicalCounterpartLibrary({
  title,
  addNewLabel,
  items,
  loading,
  tableKey,
  onAddNew,
  searchPlaceholder,
}: ChemicalCounterpartLibraryProps): JSX.Element {
  const [query, setQuery] = useState('');

  const filteredItems = query
    ? items.filter(
        (item) =>
          item.name.toLowerCase().includes(query.toLowerCase()) ||
          (item?.code ?? '').toLowerCase().includes(query.toLowerCase())
      )
    : items;

  const rows: Array<ChemicalCounterpartRow> = filteredItems.map((item) => ({
    id: item.id,
    extra: {calculatedMolecularWeight: item.calculatedMolecularWeight},
    data: {
      name: item.name,
      code: item.code ?? '',
      chemicalStructure: (
        <ChemicalStructureSVG
          chemicalStructureImage={item.imageData}
          className={css(styles.chemicalStructureContainer)}
          svgClassName={css(styles.chemicalStructureSvg)}
        />
      ),
      molecularFormula: formatMolecularFormula(item.molecularFormula ?? ''),
      molecularWeight: item.calculatedMolecularWeight?.toFixed(MOLECULAR_WEIGHT_PRECISION),
      canonicalSmiles: item.canonicalSmiles,
      id: item.id,
      actions: <ChemicalCounterpartActions id={item.id} name={item.name} />,
    },
  }));

  return (
    <Stack h="100%">
      <Box mx={SIDE_PADDING_PX}>
        {/* The Mantine fontsize theme is really large, temporarily hard-code the size until it's updated. */}
        <Title order={1} style={{fontSize: '24px'}}>
          {title}
        </Title>
        {loading ? (
          <Flex justify="center">
            <Loader />
          </Flex>
        ) : (
          <Flex justify="space-between" my={12}>
            <PlainSearchInput
              clearType={ClearType.X}
              onChange={(e: InputChangeEvent) => {
                setQuery(e.target.value);
              }}
              onEscape={() => {
                setQuery('');
              }}
              placeholder={searchPlaceholder}
              value={query}
            />
            <Group>
              <Tooltip label={EXPORT_AUDIT_LOG_DISPLAY_STRING}>
                <ActionIcon
                  aria-label={EXPORT_AUDIT_LOG_DISPLAY_STRING}
                  variant="tertiary"
                  onClick={() => {
                    exportAuditLogs(items.map((item) => item.id));
                  }}
                >
                  <BenchlingIcon icon="audit-trail" />
                </ActionIcon>
              </Tooltip>
              <Button variant="primary" size="sm" onClick={onAddNew}>
                {addNewLabel}
              </Button>
            </Group>
          </Flex>
        )}
      </Box>
      {!loading && (
        <SortableScrollableTable
          canResizeColumns={true}
          columns={COLUMNS}
          getRowContentHeight={() => ROW_HEIGHT}
          emptyTableText="No items found."
          getSortValue={(row: ChemicalCounterpartRow, sortKey: string) => {
            switch (sortKey) {
              case 'name':
              case 'code':
                return row.data[sortKey];
              case 'molecularWeight':
                return row.extra.calculatedMolecularWeight ?? Infinity;
              default:
                return '';
            }
          }}
          initialRows={rows}
          initialSort={[{sortKey: 'name'}]}
          tableKey={tableKey}
          calcLeftPadding={() => SIDE_PADDING}
        />
      )}
    </Stack>
  );
}

const styles = StyleSheet.create({
  chemicalStructureContainer: {
    height: `${ROW_HEIGHT}px`,
    textAlign: 'center',
  },
  chemicalStructureSvg: {
    width: '100%',
    height: '100%',
  },
});
